/**
 * Preload the `@font-face` rules that get embedded in a capture SVG into the
 * capturing document's `document.fonts`, and await their decode, BEFORE the
 * SVG is rasterized to a canvas.
 *
 * Why: the capture paths (`capture-svg-preview.ts`, `capture-html-preview.ts`)
 * embed base64 `@font-face` rules inside a `data:image/svg+xml` document and
 * load it via `new Image()`. An `<img>`-loaded SVG is an isolated document with
 * no access to the parent page's `document.fonts`, so it must decode the
 * embedded font bytes itself. Critically, `img.onload` for an SVG resolves as
 * soon as the markup parses — NOT when the embedded fonts finish decoding. An
 * uncompressed OTF/TTF is slow to decode, so `drawImage` runs against the
 * system fallback font (typically wider than a condensed brand font, producing
 * overlapping / collapsed text). Preloading the same font bytes into the
 * capturing document and awaiting `FontFace.load()` guarantees the bytes are
 * decoded before the draw, so the correct brand font is rasterized.
 *
 * This is best-effort: a single font that fails to load must never abort the
 * export, so each load is wrapped so one failure is logged and skipped.
 */

/**
 * Matches an `@font-face { ... }` block, capturing its body. `[^}]*` is
 * sufficient because `@font-face` bodies are flat property/value pairs — nested
 * braces / at-rules are not allowed inside them.
 */
const FONT_FACE_BLOCK_RE = /@font-face\s*\{([^}]*)\}/gi;

/**
 * Matches `font-family: <value>;` inside a `@font-face` body, capturing the
 * (optionally quoted) family name.
 */
const FONT_FAMILY_RE = /font-family\s*:\s*(?:"([^"]+)"|'([^']+)'|([^;]+))/i;

/**
 * Matches the first `url(...)` token inside a `@font-face` body, capturing the
 * (optionally quoted) URL — a `data:` URL in the embedded rules.
 */
const URL_RE = /url\(\s*(?:"([^"]+)"|'([^']+)'|([^)'"\s]+))\s*\)/i;

/** Matches `font-weight: <value>;` inside a `@font-face` body (value may be a
 * range like `100 900`). */
const FONT_WEIGHT_RE = /font-weight\s*:\s*([^;]+)/i;

/** Matches `font-style: <value>;` inside a `@font-face` body. */
const FONT_STYLE_RE = /font-style\s*:\s*([^;]+)/i;

type ParsedFontFace = {
  family: string;
  url: string;
  /** Descriptors carried verbatim from the rule so the preloaded face matches
   * the same weight/style the on-screen provider registered — otherwise a bare
   * default-weight (400/normal) face would be added alongside it. */
  descriptors: FontFaceDescriptors;
};

export function parseFontFaces(cssRules: string[]): ParsedFontFace[] {
  const faces: ParsedFontFace[] = [];
  for (const rule of cssRules) {
    for (const block of rule.matchAll(FONT_FACE_BLOCK_RE)) {
      const body = block[1] ?? "";
      const familyMatch = FONT_FAMILY_RE.exec(body);
      const urlMatch = URL_RE.exec(body);
      const family = (
        familyMatch?.[1] ??
        familyMatch?.[2] ??
        familyMatch?.[3] ??
        ""
      ).trim();
      const url = (
        urlMatch?.[1] ??
        urlMatch?.[2] ??
        urlMatch?.[3] ??
        ""
      ).trim();
      if (!family || !url) continue;
      const descriptors: FontFaceDescriptors = {};
      const weight = FONT_WEIGHT_RE.exec(body)?.[1]?.trim();
      if (weight) descriptors.weight = weight;
      const style = FONT_STYLE_RE.exec(body)?.[1]?.trim();
      if (style) descriptors.style = style;
      faces.push({ family, url, descriptors });
    }
  }
  return faces;
}

/**
 * Best-effort: parse the embedded `@font-face` rules, load each into the
 * capturing document's `document.fonts`, and await their decode. Never throws —
 * a font that fails to load is logged and skipped so the export always
 * proceeds.
 *
 * Returns the faces it added so the caller can remove them via
 * {@link removePreloadedFonts} once the capture is done — the faces are only
 * needed for the isolated SVG's decode, and leaving them in `document.fonts`
 * across repeated captures leaks registry entries (and, at the wrong
 * descriptors, could skew later on-screen rendering).
 */
export async function preloadFontsIntoDocument(
  cssRules: string[],
): Promise<FontFace[]> {
  if (typeof document === "undefined" || !document.fonts) return [];
  if (typeof FontFace === "undefined") return [];

  const faces = parseFontFaces(cssRules);
  if (faces.length === 0) return [];

  const added = await Promise.all(
    faces.map(async ({ family, url, descriptors }) => {
      try {
        const face = new FontFace(family, `url(${url})`, descriptors);
        await face.load();
        document.fonts.add(face);
        return face;
      } catch (error) {
        // A single font failure must not abort the export — the capture then
        // falls back to whatever the isolated SVG document can decode.
        console.warn(
          `preloadFontsIntoDocument: failed to load font "${family}"`,
          error,
        );
        return null;
      }
    }),
  );

  return added.filter((face): face is FontFace => face !== null);
}

/**
 * Remove faces added by {@link preloadFontsIntoDocument}. Best-effort — safe to
 * call with an empty array or where `document.fonts` is unavailable.
 */
export function removePreloadedFonts(faces: FontFace[]): void {
  if (typeof document === "undefined" || !document.fonts) return;
  for (const face of faces) {
    try {
      document.fonts.delete(face);
    } catch {
      // A face that was never added (or already removed) is not an error.
    }
  }
}
